import { AutocompleteWithVirtualList } from '@modules/common/components/AutocompleteWithVirtualList';
import { KebabMenu } from '@modules/common/components/KebabMenu';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { PrivateSlackChannelConnectionAlert } from '@modules/common/components/PrivateSlackChannelConnectionAlert';
import { useFetchSlackChannels } from '@modules/integrationsV2/hooks';
import { DisconnectSlackConfirmationModal } from '@modules/subscribe/components/SlackOutboundCard/DisconnectSlackConfirmationModal';
import { RefreshRounded } from '@mui/icons-material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/system';
import { Slack } from 'common/Icons/Icons';
import {
  IntegrationChannelType,
  IntegrationDirection,
  SlackChannelInput,
  useListIntegrationsQuery,
} from 'generated/graphql';
import { capitalize } from 'lodash';
import { SyntheticEvent, useMemo, useState } from 'react';
import { IsLoading } from 'v2/core/repository/edge-server/types';
import { useGetUser } from 'v2/core/services/user';
import { SlackOutboundCardAutocompleteVirtualListRow } from './SlackOutboundCardAutocompleteVirtualListRow';
import { SlackOutboundConnectButton } from './SlackOutboundConnectButton';

interface SlackOutboundCardInputProps {
  selectedSlackChannels: SlackChannelInput[];
  onSelectedSlackChannelsChange: (slackChannels: SlackChannelInput[]) => void;
  onDropdownOpen?: () => void;
  onConnectSlackClick?: () => void;
  removeSubscription?: () => void;
  isSubcribed: boolean;
  areEmailOutboundsSelected: boolean;
}

export const SlackOutboundCardInput = ({
  selectedSlackChannels,
  onSelectedSlackChannelsChange,
  onDropdownOpen,
  onConnectSlackClick,
  removeSubscription,
  isSubcribed,
  areEmailOutboundsSelected,
}: SlackOutboundCardInputProps) => {
  const theme = useTheme();

  const { openModal } = useMuiModalContext();

  const [
    isPollingSlackChannelsInProgress,
    setIsPollingSlackChannelsInProgress,
  ] = useState(false);

  const {
    id: userID,
    tenant: { id: tenantID },
    firstName,
    lastName,
  } = useGetUser();

  const { data: listIntegrationsData, loading: isListIntegrationsLoading } =
    useListIntegrationsQuery({
      variables: {
        tenantID,
        createdBy: userID,
        directions: [IntegrationDirection.PrivateOutbound],
      },
      fetchPolicy: 'cache-and-network',
    });

  const slackPrivateOutbound =
    listIntegrationsData?.listIntegrations?.integrations?.find(
      (integration) =>
        integration.channel.type === IntegrationChannelType.Slack &&
        integration.isActive,
    );

  const {
    data: channels,
    status: channelsStatus,
    refreshID = undefined,
    pollForSlackChannels,
  } = useFetchSlackChannels(
    slackPrivateOutbound?.id ?? '',
    {
      FetchPolicy: 'network-only',
      Skip: !slackPrivateOutbound,
    },
    setIsPollingSlackChannelsInProgress,
  );

  const createdByUserID = slackPrivateOutbound?.data?.slack?.createdByUserID;

  const slackChannelsInWorkspace = useMemo(() => {
    if (createdByUserID) {
      const userDisplayName = `${capitalize(firstName)} ${capitalize(
        lastName,
      )}`;
      return [
        {
          id: createdByUserID,
          name: userDisplayName,
        },
        ...channels,
      ];
    }

    return channels;
  }, [channels, createdByUserID, firstName, lastName]);

  const renderPrimaryActionContent = () => {
    if (isListIntegrationsLoading && !listIntegrationsData) {
      return (
        <Stack alignItems="center" justifyContent="center">
          <CircularProgress size={30} />
        </Stack>
      );
    }

    if (!slackPrivateOutbound) {
      return <SlackOutboundConnectButton onClick={onConnectSlackClick} />;
    }

    return (
      <AutocompleteWithVirtualList
        fullWidth
        multiple
        limitTags={8}
        disableCloseOnSelect
        value={selectedSlackChannels}
        options={IsLoading(channelsStatus) ? [] : slackChannelsInWorkspace}
        getOptionLabel={(option) => option.name}
        onChange={onSelectionChange}
        renderTags={(channels, getTagProps) =>
          channels.map((channel, index) => (
            <Chip
              icon={
                channel.isPrivate ? (
                  <LockOutlinedIcon fontSize="small" />
                ) : undefined
              }
              label={channel.name}
              {...getTagProps({ index })}
            />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label="Slack channels" />
        )}
        renderOption={(props, option, state) =>
          [props, option, state] as React.ReactNode
        }
        VirtualListRow={(VirtualListRowProps) => (
          <SlackOutboundCardAutocompleteVirtualListRow
            VirtualListRowProps={VirtualListRowProps}
            createdByUserID={createdByUserID}
          />
        )}
        noOptionsText={
          <>
            <Typography variant="inherit">No matches found</Typography>
            <Typography variant="caption">
              A new channel may take around 5 minutes to be available.
            </Typography>
          </>
        }
        itemSize={60}
        componentsProps={{ paper: { elevation: 8 } }}
        loading={IsLoading(channelsStatus)}
        loadingText="Fetching your Slack channels. This might take up to 20 seconds."
        isOptionEqualToValue={(option, value) => option.id === value?.id}
        onOpen={onDropdownOpen}
      />
    );
  };

  const onSelectionChange = (
    _: SyntheticEvent,
    newOptions: SlackChannelInput[],
  ) => {
    onSelectedSlackChannelsChange(newOptions);
  };

  const selectedPrivateChannels = selectedSlackChannels.filter(
    (channel) => channel.isPrivate,
  );

  const refetchSlackChannels = () => {
    pollForSlackChannels(refreshID);
  };

  const isLoadingSlackChannels =
    IsLoading(channelsStatus) || isPollingSlackChannelsInProgress;

  const renderHeaderSecondaryAction = () => {
    if (isLoadingSlackChannels) {
      return <CircularProgress size={20} sx={{ color: 'text.secondary' }} />;
    }

    if (refreshID) {
      return (
        <Tooltip title="Refetch all Slack channels" arrow placement="top">
          <IconButton>
            <RefreshRounded
              fontSize="inherit"
              onClick={() => refetchSlackChannels()}
            />
          </IconButton>
        </Tooltip>
      );
    }

    return null;
  };

  const openDisconnectSlackConfirmationModal = () => {
    if (!slackPrivateOutbound) {
      return;
    }

    const shouldRemoveSubscription = isSubcribed && !areEmailOutboundsSelected;

    openModal({
      contentComponent: () => (
        <DisconnectSlackConfirmationModal
          integrationID={slackPrivateOutbound.id}
          onCompleted={() => {
            onSelectedSlackChannelsChange([]);
            if (shouldRemoveSubscription && removeSubscription) {
              removeSubscription();
            }
          }}
        />
      ),
      modalProps: {
        PaperProps: {
          elevation: 3,
        },
        fullWidth: true,
        maxWidth: 'sm',
      },
    });
  };

  return (
    <Stack spacing={2}>
      <Typography variant="body1" color="text.primary">
        <Stack
          justifyContent="space-between"
          alignItems="center"
          direction="row"
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            width="100%"
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Slack
                width="20px"
                height="20px"
                fill={theme.palette.text.primary}
              />
              <Typography variant="subtitle1">Slack</Typography>
            </Stack>
            {!!slackPrivateOutbound && !isLoadingSlackChannels && (
              <KebabMenu
                menuItems={[
                  {
                    icon: null,
                    label: <Typography>Disconnect Slack</Typography>,
                    onClick: openDisconnectSlackConfirmationModal,
                  },
                ]}
                buttonProps={{
                  size: 'small',
                }}
                menuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'right',
                  },
                }}
              />
            )}
          </Stack>
          {renderHeaderSecondaryAction()}
        </Stack>
      </Typography>
      {renderPrimaryActionContent()}
      {!!selectedPrivateChannels.length && (
        <PrivateSlackChannelConnectionAlert
          isSubscriptionReporting
          channels={selectedPrivateChannels}
        />
      )}
    </Stack>
  );
};
